const MVCObject = require("./mvcobject").MVCObject;
const SearchParameters = require("./searchparameters").SearchParameters;

/**
 * @typedef {Object} NearbyOptions
 * @property {Number} maxStores the maximum number of stores to return
 * @property {Number} maxDistanceFromUser the maximum distance allowed between the user and a store.
 */

/**
 * Constructs a new DataSearchSource
 * @constructor
 * @param {woosmap.DataSource} dataSource an instance of woosmap DataSource
 * @param {woosmap.TextSearchQuery} searchTextOptions to set initial search parameters if used with autocomplete
 * @param {NearbyOptions} nearbyOptions containing two key : maxStores and maxDistanceFromUser for the
 * nearby search if lat/lng are provided through location property.
 * @param {PolylineFilter} [polylineFilter] the PolylineFilter if you want filter on a polyline path
 * @memberOf woosmap
 * @extends woosmap.utils.MVCObject
 */
const DataSearchSource = function (dataSource, searchTextOptions, nearbyOptions, polylineFilter) {
    this.dataSource = dataSource;
    this.stores = null;

    const searchOptions = {};
    if (nearbyOptions) {
        searchOptions.maxStores = nearbyOptions.maxStores;
        searchOptions.maxDistanceFromUser = nearbyOptions.maxDistanceFromUser;
    }
    if (searchTextOptions) {
        searchOptions.querySearchOptions = searchTextOptions;
    }
    if (polylineFilter) {
        searchOptions.polylineFilter = polylineFilter;
    }
    /**
     * Contains options to searchStores
     * @type {woosmap.search.SearchParameters}
     */
    this.searchParameters = new SearchParameters(searchOptions);
    /**
     * Observes his query to launch the search into store's data.
     * @type {string}
     */
    this.autocomplete_query = null;

    /**
     * Observes his location to narrow the search around a location.
     * Needs to be completed with a maxStore and a maxDistanceFromUser in constructors option nearbyOptions
     * @type {LatLngLiteral}
     */
    this.location = null;
};

DataSearchSource.prototype = new MVCObject();

DataSearchSource.OR = "|";
DataSearchSource.AND = "&";

/**
 * Listens the changes of query.
 */
DataSearchSource.prototype.autocomplete_query_changed = function () {
    if (this.get("autocomplete_query")) this.updateTextSearchQueryAndSearch();
    else this.updateTextSearchQuery();
};

/**
 * Listens the changes of location.
 */
DataSearchSource.prototype.location_changed = function () {
    const location = this.get("location");
    if (location?.lat && location.lng) {
        this.searchParameters.lat = location.lat;
        this.searchParameters.lng = location.lng;
        this.search();
    } else {
        this.updateTextSearchQuery();
    }
};

/**
 * Updated the searchQuery value.
 * @param {string}key the key of the searchQuery's field which will be updated.
 * @param {woosmap.search.SearchQuery}value the new value of the searchQuery's field.
 *
 */
DataSearchSource.prototype.updateSearchQuery = function (key, value) {
    this.searchParameters.searchQuery[key] = value;
};

/**
 * Updated the searchQuery value and launch a search with the new value.
 * @param {string}key the key of the searchQuery's field which will be update.
 * @param {woosmap.search.SearchQuery}value the new value of the searchQuery's field.
 *
 */
DataSearchSource.prototype.updateSearchQueryAndSearch = function (key, value) {
    this.updateSearchQuery(key, value);
    this.search();
};

/**
 * Add a new field to searchTextOptions or update an existing field (new field will be used for store filtering).
 * @param {string}fieldName the new value of the searchTextOptions
 * @param {string}operator the new value of the operator for filtering
 */
DataSearchSource.prototype.addFieldToSearchTextOptions = function (fieldName, operator) {
    this.searchParameters.searchQuery.addQueryOption(fieldName, operator);
    this.updateTextSearchQueryAndSearch();
};

/**
 * Remove a field from searchTextOptions (so field will not be use anymore for filtering).
 *
 * Update stores list after removal.
 * @param {string}fieldName the name of the field you want to remove
 */
DataSearchSource.prototype.removeFieldFromSearchTextOptions = function (fieldName) {
    this.searchParameters.searchQuery.removeQueryOption(fieldName);
    this.updateTextSearchQueryAndSearch();
};

/**
 * Get the value of 'autocomplete_query' and searchTextOptions and update the SearchQuery with them.
 * Then request new store list.
 * @deprecated
 */
DataSearchSource.prototype.updateTextSearchQuery = function () {
    this.searchParameters.searchQuery.setQuery(this.get("autocomplete_query"));
};

/**
 * Get the value of 'autocomplete_query' and searchTextOptions and update the SearchQuery with them.
 * Then request new store list.
 *
 */
DataSearchSource.prototype.updateTextSearchQueryAndSearch = function () {
    this.searchParameters.searchQuery.setQuery(this.get("autocomplete_query"));
    this.search();
};

/**
 * Use the woosmap API to search into store's data with nearbyOptions, polylineFilter, searchQuery and location
 * as parameters.
 * @param {woosmap.search.SearchQuery} [querySearchParameters] an object containing a query for search.
 * If no searchParameters is pass then DataSearchSource will use searchQuery property
 */
DataSearchSource.prototype.search = function (querySearchParameters) {
    if (querySearchParameters) {
        this.searchParameters.searchQuery = querySearchParameters;
    }
    this.dataSource.searchStoresByParameters(this.searchParameters, (geojson) => {
        this.set("stores", geojson.features);
    });
};

/**
 * Add or update the polylineFilter for the searchSource.
 *
 * To remove the filter set is value to `null`.
 * @param {PolylineFilter} polylineFilter the PolylineFilter to filter on.
 *
 */
DataSearchSource.prototype.setPolylineFilter = function (polylineFilter) {
    this.searchParameters.polylineFilter = polylineFilter;
};

/**
 * Update the maxStores value.
 *
 * To remove the search option set is value to `null`.
 * @param {Number} maxStores the new maxStores value.
 *
 */
DataSearchSource.prototype.setMaxStores = function (maxStores) {
    this.searchParameters.maxStores = maxStores;
};

/**
 * Update the maxDistanceFromUser value.
 *
 * To remove the search option set is value to `null`.
 * @param {Number} maxDistanceFromUser the new maxDistanceFromUser value.
 *
 */
DataSearchSource.prototype.setMaxDistanceFromUser = function (maxDistanceFromUser) {
    this.searchParameters.maxDistanceFromUser = maxDistanceFromUser;
};

exports.DataSearchSource = DataSearchSource;
